import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { TFunction } from "i18next";
import { InputHTMLAttributes, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { radius } from "src/design/tokens";
import {
  ConfirmDialog,
  DataTable,
  EmptyState,
  FilterPills,
  InlineAlert,
  Loader,
  LoadingButton,
  QueryErrorAlert,
  QueryStateView,
  SideDrawer,
} from "src/grading/components";
import DistrictHeader from "src/grading/pages/Districts/DistrictHeader";
import {
  errorCodeCopy,
  errorCodeOf,
  errorMessage,
} from "src/grading/utils/apiError";
import {
  DataRequestEvent,
  DataRequestRecord,
  DataRequestStatus,
  DistrictStudentMatch,
} from "src/models/hierarchy";
import { useAppDispatch } from "src/store/hooks";
import { useGetDistrictQuery } from "src/store/reducers/districts/districtsApiSlice";
import {
  useCancelDataRequestMutation,
  useCreateDataRequestMutation,
  useDownloadDataRequestMutation,
  useGetDataRequestEventsQuery,
  useListDataRequestsQuery,
  useRetryDataRequestMutation,
} from "src/store/reducers/districts/privacyApiSlice";
import { clearBreadcrumbs } from "src/store/reducers/navigation/navigationSlice";
import StudentSearchField from "./StudentSearchField";

/** The list refreshes on its own only while a request is pending or running (FR-049). */
const POLL_MS = 10_000;
const ACTIVE: DataRequestStatus[] = ["pending", "running"];

type RequestType = "export" | "delete";
type RequestScope = "district" | "student";

interface CreateBody {
  type: RequestType;
  scope: RequestScope;
  studentId?: string;
  confirmation?: string;
}

/** Status pill copy keys; `failed` reads as "Stopped" (console copy never says "failed"). */
const STATUS_KEY: Record<DataRequestStatus, string> = {
  pending: "pending",
  running: "running",
  completed: "completed",
  failed: "stopped",
  cancelled: "cancelled",
  expired: "expired",
};

/** History sentences by event action (research R18). */
const EVENT_KEY: Record<string, string> = {
  created: "created",
  started: "started",
  "step-completed": "stepCompleted",
  completed: "completed",
  failed: "stopped",
  cancelled: "cancelled",
  retried: "retried",
  "download-link-created": "downloadLinkCreated",
  expired: "expired",
};

const formatDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString() : "";

/** Plain-language reason for a stopped request; codes without copy read as UNKNOWN (FR-022). */
const stoppedReason = (t: TFunction, code: string | null | undefined) =>
  errorCodeCopy(t, code) ?? t("errors.UNKNOWN");

/** A refused request: its copy, and whether it was the duplicate refusal (FR-021). */
interface Refusal {
  message: string;
  duplicate: boolean;
}

const refusalOf = (t: TFunction, error: unknown): Refusal => ({
  message: errorMessage(
    t,
    error,
    t("dataRequests.createError", {
      defaultValue: "Could not create the request",
    }),
  ),
  duplicate: errorCodeOf(error) === "DUPLICATE_REQUEST",
});

const RefusalAlert = ({ refusal }: { refusal: Refusal | null }) =>
  refusal ? (
    <Box
      data-testid={refusal.duplicate ? "duplicate-refused" : "request-error"}
    >
      <InlineAlert severity={refusal.duplicate ? "warning" : "error"}>
        {refusal.message}
      </InlineAlert>
    </Box>
  ) : null;

const RequestStatusPill = ({ status }: { status: DataRequestStatus }) => {
  const { t } = useTranslation();
  return (
    <Box
      component="span"
      sx={(theme) => {
        const p = theme.palette;
        const tone =
          status === "running"
            ? { bg: p.primary.light, fg: p.primaryInk }
            : status === "completed"
              ? { bg: p.success.light, fg: p.successInk }
              : status === "failed"
                ? { bg: p.warning.light, fg: p.warning.dark }
                : { bg: p.surfaceAlt, fg: p.text.primary };
        return {
          display: "inline-flex",
          px: 1.25,
          py: 0.5,
          borderRadius: `${radius.pill}px`,
          bgcolor: tone.bg,
          color: tone.fg,
          fontSize: 11,
          fontWeight: 800,
          lineHeight: 1,
          textTransform: "uppercase",
          letterSpacing: ".14em",
          whiteSpace: "nowrap",
        };
      }}
    >
      {t(`dataRequests.status.${STATUS_KEY[status] ?? "pending"}`)}
    </Box>
  );
};

/** The status cell: the pill, then progress, the reason it stopped, or how long a file is kept. */
const StatusCell = ({ request }: { request: DataRequestRecord }) => {
  const { t } = useTranslation();
  const note = (text: string) => (
    <Typography
      variant="caption"
      display="block"
      sx={{ mt: 0.75, color: "text.secondary", maxWidth: 320 }}
    >
      {text}
    </Typography>
  );
  const { progress } = request;
  const percent =
    progress && progress.studentsTotal > 0
      ? Math.min(
          100,
          Math.round((progress.studentsDone / progress.studentsTotal) * 100),
        )
      : null;

  return (
    <>
      <RequestStatusPill status={request.status} />
      {request.status === "running" && progress ? (
        <Box sx={{ mt: 0.75, maxWidth: 220 }}>
          {percent !== null ? (
            <LinearProgress
              variant="determinate"
              value={percent}
              aria-label={t("dataRequests.progressLabel")}
              sx={{ borderRadius: `${radius.pill}px` }}
            />
          ) : null}
          {note(
            t("dataRequests.progress", {
              done: progress.studentsDone,
              total: progress.studentsTotal,
            }),
          )}
        </Box>
      ) : null}
      {request.status === "failed"
        ? note(stoppedReason(t, request.errorCode))
        : null}
      {request.status === "completed" &&
      request.type === "export" &&
      request.expiresAt
        ? note(
            t("dataRequests.availableUntil", {
              date: new Date(request.expiresAt).toLocaleDateString(),
            }),
          )
        : null}
      {request.status === "expired"
        ? note(
            request.expiredReason === "student-deleted"
              ? t("dataRequests.expiredStudentDeleted")
              : t("dataRequests.expiredAge"),
          )
        : null}
    </>
  );
};

/** What "Delete district data" removes and keeps (FR-016). */
const RemovedAndKept = () => {
  const { t } = useTranslation();
  const uid = useId();
  const group = (
    id: string,
    heading: string,
    items: string[],
    tone: "dangerInk" | "text.primary",
  ) => (
    <Box>
      <Typography
        id={id}
        component="h3"
        sx={{ m: 0, mb: 0.75, fontSize: 14, fontWeight: 800, color: tone }}
      >
        {heading}
      </Typography>
      <Box
        component="ul"
        aria-labelledby={id}
        sx={{
          m: 0,
          pl: 2.5,
          fontSize: 14,
          lineHeight: 1.5,
          color: "text.primary",
        }}
      >
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </Box>
    </Box>
  );
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 2,
      }}
    >
      {group(
        `${uid}-removed`,
        t("dataRequests.removed"),
        [
          t("dataRequests.removedStudents"),
          t("dataRequests.removedResults"),
          t("dataRequests.removedDocuments"),
          t("dataRequests.removedVideos"),
          t("dataRequests.removedRoster"),
        ],
        "dangerInk",
      )}
      {group(
        `${uid}-kept`,
        t("dataRequests.kept"),
        [
          t("dataRequests.keptSchools"),
          t("dataRequests.keptClasses"),
          t("dataRequests.keptStaff"),
        ],
        "text.primary",
      )}
    </Box>
  );
};

const sameName = (a: string, b: string) =>
  a.trim().toLowerCase() === b.trim().toLowerCase();

interface RequestDialogProps {
  open: boolean;
  type: RequestType;
  districtId: string;
  loading: boolean;
  onSubmit: (body: CreateBody) => Promise<Refusal | null>;
  onClose: () => void;
}

/**
 * Creates an export or a delete for the district or one student (FR-017). The student is found by
 * name or SIS id; a delete asks for the student's name (or the district id) to be typed.
 */
const RequestDialog = ({
  open,
  type,
  districtId,
  loading,
  onSubmit,
  onClose,
}: RequestDialogProps) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [scope, setScope] = useState<RequestScope>("district");
  const [student, setStudent] = useState<DistrictStudentMatch | null>(null);
  const [confirmation, setConfirmation] = useState("");
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  useEffect(() => {
    if (open) {
      setScope(type === "delete" ? "student" : "district");
      setStudent(null);
      setConfirmation("");
      setRefusal(null);
    }
  }, [open, type]);

  const isDelete = type === "delete";
  const target =
    scope === "student" ? (student?.displayName ?? null) : districtId;
  const confirmed =
    !isDelete ||
    (target !== null &&
      (scope === "student"
        ? sameName(confirmation, target)
        : confirmation.trim() === target));
  const ready = (scope === "district" || student !== null) && confirmed;

  const submit = async () => {
    setRefusal(null);
    const body: CreateBody = { type, scope };
    if (scope === "student" && student) body.studentId = student.id;
    if (isDelete) body.confirmation = confirmation.trim();
    setRefusal(await onSubmit(body));
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby={titleId}
      PaperProps={{ "data-testid": "request-dialog" } as Record<string, string>}
    >
      <DialogTitle id={titleId} component="h2" sx={{ fontSize: 24 }}>
        {isDelete
          ? t("dataRequests.deleteTitle")
          : t("dataRequests.exportTitle")}
      </DialogTitle>
      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}
      >
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
          {isDelete
            ? t("dataRequests.deleteIntro")
            : t("dataRequests.exportIntro")}
        </Typography>
        <Stack spacing={1}>
          <Typography
            component="span"
            sx={{ fontSize: 13, fontWeight: 700 }}
            aria-hidden="true"
          >
            {t("dataRequests.colScope")}
          </Typography>
          <FilterPills
            size="sm"
            data-testid="request-scope"
            aria-label={t("dataRequests.colScope")}
            options={[
              {
                value: "district",
                label: t("dataRequests.scopeWholeDistrict"),
              },
              { value: "student", label: t("dataRequests.scopeOneStudent") },
            ]}
            value={scope}
            onChange={(next) => {
              setScope(next as RequestScope);
              setConfirmation("");
              setRefusal(null);
            }}
          />
        </Stack>
        {scope === "student" ? (
          <StudentSearchField
            districtId={districtId}
            value={student}
            onChange={(next) => {
              setStudent(next);
              setConfirmation("");
            }}
            data-testid="student-search"
          />
        ) : null}
        {isDelete && scope === "district" ? <RemovedAndKept /> : null}
        {isDelete && scope === "student" ? (
          <InlineAlert severity="warning">
            {t("dataRequests.deleteStudentBody")}
          </InlineAlert>
        ) : null}
        {isDelete && target !== null ? (
          <TextField
            fullWidth
            size="small"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            label={t("console.typeToConfirm", { word: target })}
            autoComplete="off"
            inputProps={
              {
                "data-testid": "request-confirmation",
              } as InputHTMLAttributes<HTMLInputElement>
            }
          />
        ) : null}
        <RefusalAlert refusal={refusal} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1.25 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          {t("common.cancel")}
        </Button>
        <LoadingButton
          variant="contained"
          color={isDelete ? "error" : "secondary"}
          loading={loading}
          disabled={!ready}
          onClick={submit}
          data-testid="request-submit"
          label={isDelete ? t("dataRequests.delete") : t("dataRequests.export")}
        />
      </DialogActions>
    </Dialog>
  );
};

interface DeleteDistrictDialogProps {
  open: boolean;
  districtId: string;
  loading: boolean;
  onSubmit: (body: CreateBody) => Promise<Refusal | null>;
  onClose: () => void;
}

/** "Delete district data": lists what is removed and kept, and needs the district id typed (FR-016). */
const DeleteDistrictDialog = ({
  open,
  districtId,
  loading,
  onSubmit,
  onClose,
}: DeleteDistrictDialogProps) => {
  const { t } = useTranslation();
  const [typed, setTyped] = useState("");
  const [refusal, setRefusal] = useState<Refusal | null>(null);

  useEffect(() => {
    if (open) {
      setTyped("");
      setRefusal(null);
    }
  }, [open]);

  return (
    <ConfirmDialog
      open={open}
      tone="destructive"
      eyebrow={t("dataRequests.cannotUndo")}
      title={t("district.deleteData")}
      body={
        <Stack spacing={2}>
          <span>{t("dataRequests.deleteDistrictBody")}</span>
          <RemovedAndKept />
        </Stack>
      }
      confirmLabel={t("dataRequests.delete")}
      testIdPrefix="delete-district"
      confirmTestId="delete-confirm"
      confirmDisabled={typed.trim() !== districtId}
      loading={loading}
      onConfirm={async () => {
        setRefusal(null);
        setRefusal(
          await onSubmit({
            type: "delete",
            scope: "district",
            confirmation: typed.trim(),
          }),
        );
      }}
      onClose={onClose}
    >
      <TextField
        fullWidth
        size="small"
        value={typed}
        onChange={(event) => setTyped(event.target.value)}
        label={t("console.typeToConfirm", { word: districtId })}
        autoComplete="off"
        inputProps={
          {
            "data-testid": "typed-confirmation",
          } as InputHTMLAttributes<HTMLInputElement>
        }
      />
      <RefusalAlert refusal={refusal} />
    </ConfirmDialog>
  );
};

/** One history entry as a sentence; actions without a known sentence read "Updated". */
const eventSentence = (t: TFunction, event: DataRequestEvent) =>
  t(`dataRequests.event.${EVENT_KEY[event.action] ?? "other"}`, {
    actor: event.actorName || t("dataRequests.event.system"),
  });

const EventsDrawer = ({
  districtId,
  request,
  onClose,
}: {
  districtId: string;
  request: DataRequestRecord | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const events = useGetDataRequestEventsQuery(
    { districtId, requestId: request?.id ?? "" },
    { skip: !request },
  );
  const items = events.data ?? [];

  return (
    <SideDrawer
      open={request !== null}
      onClose={onClose}
      title={t("dataRequests.historyTitle")}
      subtitle={
        request
          ? `${t(`dataRequests.typeLabel.${request.type}`)} · ${formatDate(
              request.createdAt,
            )}`
          : undefined
      }
    >
      {events.isLoading ? (
        <Loader />
      ) : events.isError ? (
        <QueryErrorAlert query={events}>
          {t("dataRequests.historyError")}
        </QueryErrorAlert>
      ) : items.length === 0 ? (
        <Typography sx={{ color: "text.secondary" }}>
          {t("dataRequests.historyEmpty")}
        </Typography>
      ) : (
        <Box
          component="ol"
          aria-label={t("dataRequests.historyTitle")}
          sx={{ listStyle: "none", m: 0, p: 0 }}
        >
          {items.map((event, index) => {
            const code =
              event.action === "failed" &&
              typeof event.detail?.errorCode === "string"
                ? event.detail.errorCode
                : null;
            return (
              <Box
                component="li"
                key={`${event.at}-${index}`}
                sx={(theme) => ({
                  py: 1.5,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                })}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  {eventSentence(t, event)}
                </Typography>
                {code ? (
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    {stoppedReason(t, code)}
                  </Typography>
                ) : null}
                <Typography
                  component="time"
                  dateTime={event.at}
                  sx={{ fontSize: 13, color: "text.secondary" }}
                >
                  {formatDate(event.at)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </SideDrawer>
  );
};

/**
 * Data requests (contracts/console-district-tabs.md, `data-requests`): export or delete the
 * district's or one student's data, follow each request, download a finished export, cancel a
 * queued request, retry a stopped one and read its history.
 */
const DataRequestsPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { districtId } = useParams<{ districtId: string }>();

  const [poll, setPoll] = useState(false);
  const list = useListDataRequestsQuery(districtId, {
    pollingInterval: poll ? POLL_MS : 0,
  });
  const requests = list.data;
  const active = (requests ?? []).some((r) => ACTIVE.includes(r.status));
  useEffect(() => {
    setPoll(active);
  }, [active]);

  // Secondary: the header's name and meta. Its failure is said inline under the header (FR-048).
  const districtQuery = useGetDistrictQuery(districtId, {
    skip: !districtId,
  });
  const district = districtQuery.data;
  const [createRequest, { isLoading: creating }] =
    useCreateDataRequestMutation();
  const [downloadRequest] = useDownloadDataRequestMutation();
  const [cancelRequest] = useCancelDataRequestMutation();
  const [retryRequest] = useRetryDataRequestMutation();

  const [dialogType, setDialogType] = useState<RequestType | null>(null);
  const [deletingDistrict, setDeletingDistrict] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState(false);
  const [eventsFor, setEventsFor] = useState<DataRequestRecord | null>(null);

  // Every district tab shares the district header and its tabs, so none shows a breadcrumb trail
  // (it overlapped the header's eyebrow).
  useEffect(() => {
    dispatch(clearBreadcrumbs());
    return () => {
      dispatch(clearBreadcrumbs());
    };
  }, [dispatch]);

  /** Creates a request; resolves to the refusal to show, or null once it was accepted. */
  const create = async (body: CreateBody): Promise<Refusal | null> => {
    setActionError(null);
    setNotice(false);
    try {
      await createRequest({ districtId, body }).unwrap();
      setDialogType(null);
      setDeletingDistrict(false);
      setNotice(true);
      return null;
    } catch (error) {
      return refusalOf(t, error);
    }
  };

  /** Runs a row action, reporting a refusal or an outage in the page alert. */
  const rowAction = async (
    key: string,
    run: () => Promise<unknown>,
    fallback: string,
  ) => {
    setActionError(null);
    setNotice(false);
    setBusy(key);
    try {
      await run();
    } catch (error) {
      setActionError(errorMessage(t, error, fallback));
    } finally {
      setBusy(null);
    }
  };

  const download = (request: DataRequestRecord) =>
    rowAction(
      `download:${request.id}`,
      async () => {
        const { url } = await downloadRequest({
          districtId,
          requestId: request.id,
        }).unwrap();
        if (!url) throw new Error("no download link");
        // Same-tab navigation: a presigned attachment downloads without a popup (FR-023).
        window.location.assign(url);
      },
      t("dataRequests.downloadError"),
    );

  const cancel = (request: DataRequestRecord) =>
    rowAction(
      `cancel:${request.id}`,
      () => cancelRequest({ districtId, requestId: request.id }).unwrap(),
      t("dataRequests.cancelError"),
    );

  const retry = (request: DataRequestRecord) =>
    rowAction(
      `retry:${request.id}`,
      () => retryRequest({ districtId, requestId: request.id }).unwrap(),
      t("dataRequests.retryError"),
    );

  const openDialog = (type: RequestType) => {
    setActionError(null);
    setNotice(false);
    setDialogType(type);
  };

  return (
    <QueryStateView query={list}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.75 }}>
        <DistrictHeader districtId={districtId} district={district} />
        {districtQuery.isError && !district ? (
          <QueryErrorAlert
            query={districtQuery}
            data-testid="district-details-error"
          >
            {t("district.detailsLoadError")}
          </QueryErrorAlert>
        ) : null}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              flex: "1 1 360px",
              fontSize: 14,
              lineHeight: 1.45,
              color: "text.secondary",
            }}
          >
            {t("dataRequests.description")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              data-testid="create-export"
              onClick={() => openDialog("export")}
            >
              {t("district.createExport")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              data-testid="create-delete"
              onClick={() => openDialog("delete")}
            >
              {t("dataRequests.deleteStudentData")}
            </Button>
            <Button
              variant="outlined"
              color="error"
              data-testid="delete-district"
              onClick={() => {
                setActionError(null);
                setNotice(false);
                setDeletingDistrict(true);
              }}
            >
              {t("district.deleteData")}
            </Button>
          </Box>
        </Box>

        {actionError ? (
          <InlineAlert severity="error">{actionError}</InlineAlert>
        ) : null}
        {notice ? (
          <InlineAlert severity="success">
            {t("dataRequests.created")}
          </InlineAlert>
        ) : null}

        <Paper sx={{ overflow: "hidden" }}>
          <DataTable
            aria-label={t("district.dataRequests")}
            dense
            rows={requests ?? []}
            getRowKey={(r) => r.id}
            getRowData={(r) => ({
              "data-testid": "request-row",
              "data-status": r.status,
              "data-request-id": r.id,
            })}
            emptyState={
              <EmptyState
                title={t("dataRequests.empty")}
                body={t("dataRequests.emptyBody")}
              />
            }
            columns={[
              {
                key: "createdAt",
                header: t("dataRequests.colCreated"),
                render: (r) => formatDate(r.createdAt),
              },
              {
                key: "request",
                header: t("dataRequests.colRequest"),
                render: (r) => (
                  <>
                    <Typography
                      component="span"
                      display="block"
                      sx={{ fontSize: 14, fontWeight: 700 }}
                    >
                      {t(`dataRequests.typeLabel.${r.type}`)}
                    </Typography>
                    <Typography
                      component="span"
                      display="block"
                      sx={{ fontSize: 13, color: "text.secondary" }}
                    >
                      {r.scope === "student"
                        ? (r.studentName ?? t("dataRequests.studentRemoved"))
                        : t("dataRequests.scopeWholeDistrict")}
                    </Typography>
                  </>
                ),
              },
              {
                key: "requestedBy",
                header: t("dataRequests.colRequestedBy"),
                render: (r) => r.requestedByName ?? "",
              },
              {
                key: "status",
                header: t("dataRequests.colStatus"),
                render: (r) => <StatusCell request={r} />,
              },
              {
                key: "actions",
                header: (
                  <Box component="span" sx={visuallyHidden}>
                    {t("dataRequests.colActions")}
                  </Box>
                ),
                render: (r) => (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                      justifyContent: "flex-end",
                    }}
                  >
                    {r.type === "export" && r.status === "completed" ? (
                      <LoadingButton
                        size="small"
                        variant="contained"
                        data-testid="request-download"
                        loading={busy === `download:${r.id}`}
                        onClick={() => download(r)}
                        label={t("dataRequests.download")}
                      />
                    ) : null}
                    {r.status === "pending" ? (
                      <LoadingButton
                        size="small"
                        variant="outlined"
                        color="error"
                        data-testid="request-cancel"
                        loading={busy === `cancel:${r.id}`}
                        onClick={() => cancel(r)}
                        label={t("dataRequests.cancelRequest")}
                      />
                    ) : null}
                    {r.status === "failed" ? (
                      <LoadingButton
                        size="small"
                        variant="outlined"
                        data-testid="request-retry"
                        loading={busy === `retry:${r.id}`}
                        onClick={() => retry(r)}
                        label={t("dataRequests.retry")}
                      />
                    ) : null}
                    <Button
                      size="small"
                      color="inherit"
                      data-testid="request-events"
                      onClick={() => setEventsFor(r)}
                    >
                      {t("dataRequests.history")}
                    </Button>
                  </Box>
                ),
              },
            ]}
          />
        </Paper>

        <RequestDialog
          open={dialogType !== null}
          type={dialogType ?? "export"}
          districtId={districtId}
          loading={creating}
          onSubmit={create}
          onClose={() => setDialogType(null)}
        />
        <DeleteDistrictDialog
          open={deletingDistrict}
          districtId={districtId}
          loading={creating}
          onSubmit={create}
          onClose={() => setDeletingDistrict(false)}
        />
        <EventsDrawer
          districtId={districtId}
          request={eventsFor}
          onClose={() => setEventsFor(null)}
        />
      </Box>
    </QueryStateView>
  );
};

export default DataRequestsPage;
